import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, FileText, PlayCircle, HelpCircle, ShoppingBag, Mail,
  LogOut, Plus, Pencil, Trash2, X, Loader2, UploadCloud,
} from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import api, { listContent, createContent, updateContent, deleteContent, uploadImage, mediaUrl } from "../../lib/api";
import { SCHEMAS, toForm, fromForm } from "../../lib/adminSchemas";

const TABS = [
  { key: "products", label: "Products", icon: Package },
  { key: "blog", label: "Blog", icon: FileText },
  { key: "tutorials", label: "Tutorials", icon: PlayCircle },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "messages", label: "Messages", icon: Mail },
];
const fmt = (n) => `$${Number(n).toFixed(2)}`;

function ImageField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { url } = await uploadImage(fd);
      onChange(url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={mediaUrl(value)} alt="" className="w-16 h-16 rounded-md object-cover border border-[#eaeaea]" />
        ) : (
          <div className="w-16 h-16 rounded-md bg-[#f4f7f8] border border-dashed border-[#d7e6ea]" />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 text-[14px] font-serif-body text-[#0f9fbc] border border-[#0f9fbc] px-3 py-2 rounded-md hover:bg-[#0f9fbc] hover:text-white transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </label>
      </div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL"
        className="mt-2 w-full font-serif-body text-[13px] px-3 py-2 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6]" />
    </div>
  );
}

function Editor({ coll, item, onClose, onSaved }) {
  const schema = SCHEMAS[coll];
  const [form, setForm] = useState(() => (item ? toForm(item, schema.fields) : toForm({}, schema.fields)));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = fromForm(form, schema.fields);
      if (item?.id) await updateContent(coll, item.id, { ...item, ...body });
      else await createContent(coll, body);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div className="bg-white rounded-[12px] w-full max-w-2xl p-6 my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-display text-[#222] text-xl font-bold">
            {item ? "Edit" : "New"} {schema.singular}
          </h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#333]"><X size={22} /></button>
        </div>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {schema.fields.map((field) => (
            <div key={field.key}>
              <label className="block font-serif-body text-[13px] text-[#555] mb-1">{field.label}</label>
              {field.type === "textarea" || field.type === "paragraphs" || field.type === "lines" || field.type === "faqitems" ? (
                <textarea rows={field.type === "textarea" ? 3 : 5} value={form[field.key]} onChange={(e) => set(field.key, e.target.value)}
                  className="w-full font-serif-body text-[14px] px-3 py-2 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] resize-y" />
              ) : field.type === "image" ? (
                <ImageField value={form[field.key]} onChange={(v) => set(field.key, v)} />
              ) : field.type === "checkbox" ? (
                <input type="checkbox" checked={!!form[field.key]} onChange={(e) => set(field.key, e.target.checked)} className="w-5 h-5 accent-[#0f9fbc]" />
              ) : field.type === "select" ? (
                <select value={form[field.key]} onChange={(e) => set(field.key, e.target.value)}
                  className="w-full font-serif-body text-[14px] px-3 py-2 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6]">
                  <option value="">Select…</option>
                  {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={field.type === "number" ? "number" : "text"} step="0.01" value={form[field.key]} onChange={(e) => set(field.key, e.target.value)}
                  className="w-full font-serif-body text-[14px] px-3 py-2 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6]" />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="font-serif-body text-[#666] px-5 py-2.5">Cancel</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] disabled:opacity-70 text-white font-serif-body font-bold px-6 py-2.5 rounded-[4px]">
            {saving && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ContentSection({ coll }) {
  const schema = SCHEMAS[coll];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or "new"

  const load = useCallback(() => {
    setLoading(true);
    listContent(coll).then(setItems).finally(() => setLoading(false));
  }, [coll]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await deleteContent(coll, id);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-[#222] text-2xl font-bold">{schema.label}</h2>
        <button onClick={() => setEditing("new")} className="flex items-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body font-bold px-4 py-2.5 rounded-[4px]">
          <Plus size={16} /> Add {schema.singular}
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin text-[#999]" /> : (
        <div className="bg-white rounded-[10px] border border-[#eaeaea] overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[#f4f7f8] border-b border-[#eaeaea]">
              <tr>{schema.columns.map((c) => <th key={c} className="font-serif-body text-[12px] uppercase tracking-wide text-[#768487] px-4 py-3">{c}</th>)}
                <th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#f0f0f0] last:border-0">
                  {schema.columns.map((c) => (
                    <td key={c} className="font-serif-body text-[14px] text-[#333] px-4 py-3">
                      {c === "price" ? fmt(it[c]) : c === "published" ? (it[c] ? "Yes" : "No") : String(it[c] ?? "")}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(it)} className="text-[#0f9fbc] hover:text-[#008da9] p-1"><Pencil size={16} /></button>
                    <button onClick={() => remove(it.id)} className="text-[#b0b0b0] hover:text-[#d9534f] p-1 ml-2"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="font-serif-body text-[#999] text-center py-8">No items yet.</p>}
        </div>
      )}
      {editing && (
        <Editor coll={coll} item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ReadOnlySection({ endpoint, title, render }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).finally(() => setLoading(false)); }, [endpoint]);
  return (
    <div>
      <h2 className="font-serif-display text-[#222] text-2xl font-bold mb-6">{title}</h2>
      {loading ? <Loader2 className="animate-spin text-[#999]" /> : rows.length === 0 ?
        <p className="font-serif-body text-[#999]">Nothing here yet.</p> :
        <div className="space-y-4">{rows.map(render)}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { email, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#f4f7f8] flex">
      <aside className="w-60 bg-white border-r border-[#eaeaea] flex flex-col shrink-0">
        <div className="px-5 py-5 border-b border-[#eaeaea]">
          <p className="font-serif-display text-[#0f9fbc] text-lg font-bold">Life With Abba</p>
          <p className="font-serif-body text-[#999] text-[12px]">Admin</p>
        </div>
        <nav className="flex-1 py-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-5 py-3 font-serif-body text-[15px] transition-colors ${tab === t.key ? "bg-[#eaf6f8] text-[#0f9fbc] font-bold" : "text-[#555] hover:bg-[#f4f7f8]"}`}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-[#eaeaea]">
          <p className="font-serif-body text-[12px] text-[#999] truncate mb-2">{email}</p>
          <button onClick={doLogout} className="flex items-center gap-2 font-serif-body text-[14px] text-[#d9534f]"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-x-auto">
        {["products", "blog", "tutorials", "faqs"].includes(tab) && <ContentSection coll={tab} />}
        {tab === "orders" && (
          <ReadOnlySection endpoint="/orders" title="Orders" render={(o) => (
            <div key={o.id} className="bg-white rounded-[10px] border border-[#eaeaea] p-5">
              <div className="flex items-center justify-between">
                <p className="font-serif-body font-bold text-[#0f9fbc]">#{o.order_number}</p>
                <p className="font-serif-display text-[#333] font-bold">{fmt(o.total)}</p>
              </div>
              <p className="font-serif-body text-[14px] text-[#555] mt-1">{o.customer?.name} · {o.customer?.email}</p>
              <p className="font-serif-body text-[13px] text-[#888] mt-1">
                {o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}
              </p>
            </div>
          )} />
        )}
        {tab === "messages" && (
          <ReadOnlySection endpoint="/contact" title="Contact Messages" render={(m) => (
            <div key={m.id} className="bg-white rounded-[10px] border border-[#eaeaea] p-5">
              <div className="flex items-center justify-between">
                <p className="font-serif-body font-bold text-[#333]">{m.name}</p>
                <p className="font-serif-body text-[13px] text-[#888]">{m.email}</p>
              </div>
              {m.subject && <p className="font-serif-body text-[14px] text-[#0f9fbc] mt-1">{m.subject}</p>}
              <p className="font-serif-body text-[14px] text-[#555] mt-2 leading-relaxed">{m.message}</p>
            </div>
          )} />
        )}
      </main>
    </div>
  );
}
